'use client';

import type { CardProps } from '@packages/shared/src/ui/Card/Card';
import { useIntl } from 'react-intl';
import { Card, CardFooter } from '@packages/shared/src/ui/Card/Card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@packages/shared/src/ui/Collapsible/Collapsible';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { Overlay } from './Overlay/Overlay';

export type CollapsibleCardProps = {
  /** Optional children react node */
  children?: ReactNode;
  /**
   * Children for the collapsed content.
   */
  collapseChildren: ReactNode;
  /**
   * The width (horizontal) or height (vertical) of the container when collapsed.
   */
  collapsedSize?: string;
  /**
   * Text that can be clicked to extend/collapse the card.
   */
  cardActionText: {
    /**
     * Text that should be shown to extend card
     */
    expandText: string;
    /**
     * Text that should be shown to collapse card
     */
    collapseText: string;
  };
} & CardProps;

/**
 * This Component is an extension of the Card component from MUI: https://mui.com/material-ui/react-card/
 *
 * In addition to the default Card, you can add collapseChildren, which can be expanded on click.
 * */
export const CollapsibleCard = ({
  cardActionText,
  children,
  collapseChildren,
  collapsedSize,
  ...rest
}: CollapsibleCardProps) => {
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();
  const cardRef = useRef<HTMLDivElement>(null);

  const collabsibleChangeHandler = (openProp: boolean) => {
    setOpen(openProp);

    if (!openProp && cardRef.current) {
      cardRef.current.scrollIntoView();
    }
  };

  return (
    <Card {...rest} ref={cardRef}>
      {children}
      <Collapsible open={open} onOpenChange={collabsibleChangeHandler}>
        <CollapsibleContent
          {...(collapsedSize && {
            forceMount: true,
            style: {
              height: open ? 'auto' : collapsedSize,
            },
          })}
          className="relative overflow-hidden"
        >
          {!collapsedSize && <Divider />}
          {collapsedSize && <Overlay expanded={open} />}
          {collapseChildren}
        </CollapsibleContent>
        <Divider />
        <CardFooter className="px-0">
          <CollapsibleTrigger
            className="flex w-full items-center gap-1 py-1"
            aria-label={formatMessage(
              open
                ? {
                    id: 'collapsible.card.action.collapsed.aria.label',
                    defaultMessage: 'Weniger Produkteigenschaften anzeigen',
                  }
                : {
                    id: 'collapsible.card.action.expended.aria.label',
                    defaultMessage: 'Mehr Produkteigenschaften anzeigen',
                  },
            )}
          >
            <span className="text-size-body-sm pl-2">
              {/** If card is expanded then it shows the text to collapse the card and vice versa */}
              {open ? cardActionText.collapseText : cardActionText.expandText}
            </span>
            <span
              className={`icon-emp-arrow-medium-down bg-color-grey-dark h-4 w-4 ${open ? 'rotate-180' : ''}`}
            />
          </CollapsibleTrigger>
        </CardFooter>
      </Collapsible>
    </Card>
  );
};
